import { ApiGet, ApiPut, ApiPost, ApiDelete } from "../../../helpers/API/ApiData";
import { MenuPermission } from "./menuPermissions";

/**
 * Role API client for Phase 1 backend endpoints
 * Handles response normalization for consistent error handling
 */

// Response types
export interface ApiResponse<T = any> {
  data: T[] | T | { newData: T[] };
  count?: number;
}

export interface NormalizedResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  count?: number;
}



/**
 * Normalize API response to consistent format
 * Handles the ApiResponse structure from helper functions
 */
export const normalizeResponse = <T = any>(response: any): NormalizedResponse<T> => {
  if (!response) {
    return { success: false, message: "No response from server" };
  }

  // Unwrap AxiosResponse shape `{ data: ... }`
  const payload = response?.data !== undefined ? response.data : response;

  // Backend format: `{ success, data, message?, error?, count?/total? }`
  if (payload && typeof payload === "object" && typeof payload.success === "boolean") {
    return {
      ...payload,
      message: payload.message || payload.error,
      count: payload.count ?? payload.total,
    };
  }

  // Error-only shape: `{ error: string }`
  if (payload && typeof payload === "object" && payload.error && payload.success === undefined) {
    return { success: false, message: payload.error };
  }

  // Helper format: `{ data: ..., count }`
  if (payload && typeof payload === "object" && payload.data !== undefined) {
    let data = payload.data;

    // Handle newData wrapper
    if (data && typeof data === "object" && "newData" in data) {
      data = data.newData;
    }

    return {
      success: true,
      data: data,
      count: payload.count ?? payload.total,
    };
  }

  // Fallback
  return {
    success: true,
    data: payload,
  };
};

/**
 * Role (roles) endpoints
 */
export interface GetRolesQuery {
  page?: number;
  limit?: number;
  letter?: string;
}

export interface CreateRoleRequest {
  roleName: string;
  displayText: string;
  isActive: boolean;
  permissions?: MenuPermission[];
}

export interface UpdateRoleRequest {
  id: string;
  roleName?: string;
  roleDescription?: string;
  displayText?: string;
  isActive?: boolean;
  permissions?: MenuPermission[];
}

export const getRoles = async (query: GetRolesQuery = {}) => {
  const params = new URLSearchParams();
  if (query.page) params.append("page", query.page.toString());
  if (query.limit) params.append("limit", query.limit.toString());
  if (query.letter) params.append("letter", query.letter);

  const response = await ApiGet(`role/find?${params}`);
  return { data: normalizeResponse(response) };
};

export const createRole = async (data: CreateRoleRequest) => {
  const response = await ApiPost("role/create", data);
  return { data: normalizeResponse(response) };
};

export const updateRole = async (data: UpdateRoleRequest) => {
  const { id, ...payload } = data;
  const response = await ApiPut(`role/update?id=${id}`, payload);
  return { data: normalizeResponse(response) };
};

export const deleteRole = async (id: string) => {
  const response = await ApiDelete(`role/delete?id=${id}`);
  return { data: normalizeResponse(response) };
};
